<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { goalbookUrls } from '@/util/links.js'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { getSSOProviderInfo, postUserLogin } from '@/authentication/api.ts'
import { useUserStore } from '@/authentication/user.store.js'
import { useRouterStore } from '@/router/router.store.ts'
import GbDialog from '@/components/GbDialog.vue'
import Checkbox from 'primevue/checkbox'
import { useQuery } from '@tanstack/vue-query'
import InputText from 'primevue/inputtext'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import GoogleSignInButton from '@/components/GoogleSignInButton.vue'
import { SSOProviderInfo } from '@/authentication/auth_types.ts'
import { getCookie, setCrossSiteLogin } from '@/authentication/request_handler.ts'

// Static Values
const forgotPasswordUrl = goalbookUrls.forgotPassword

// Store Refs
const userStore = useUserStore()
const { ssoProviderIds, userEmail, hasGoogleOauth } = storeToRefs(userStore)
const routerStore = useRouterStore()
const { reloginRequired } = storeToRefs(routerStore)

// HTTP Data & Status
const { data: ssoProviderInfo } = useQuery({ queryFn: getSSOProviderInfo, queryKey: [] })
const loginRequestSent = ref(false)
const loginCompleted = ref(false)

const gblcs = ref('')

onMounted(async () => {
  // Set cookie for login forms
  await setCrossSiteLogin()
  gblcs.value = decodeURIComponent(getCookie('gblcs'))
})

// Form Values & Validation
const password = ref('')
const showPassword = ref(false)
const loginError = ref('')

// Computed SSO Provider Data
const ssoProviders = computed(() => {
  const ssoProviders: SSOProviderInfo[] = []

  ssoProviderIds.value?.forEach((id: string) => {
    const ssoProviderInfoForId = ssoProviderInfo?.value?.[id] as SSOProviderInfo | undefined
    if (ssoProviderInfoForId) {
      ssoProviders.push(ssoProviderInfoForId)
    }
  })

  return ssoProviders
})
const hasSSOProviders = computed(() => !!ssoProviders.value?.length)

// Analytics
const analytics = getAnalyticsTracker()
analytics.sendEvent('Login Modal Shown')

async function attemptLogin() {
  loginRequestSent.value = true
  try {
    await postUserLogin(userEmail.value, password.value, gblcs.value)
    loginCompleted.value = true
    await routerStore.handleReloginAttempt(loginCompleted.value)
    routerStore.loginSuccess = true
  } catch (e) {
    console.error(e)
    routerStore.loginSuccess = false
    loginError.value =
      'There was a problem with your login request. You may need to try again later.'
  } finally {
    loginRequestSent.value = false
  }
}

function handleVisibilityChange(isVisible: boolean) {
  if (!isVisible && !loginCompleted.value) {
    analytics?.sendEvent('Login Modal Dismissed')
    routerStore.handleReloginAttempt(loginCompleted.value)
    routerStore.loginDismissed = true
  }
}
</script>

<template>
  <GbDialog
    dialog-id="login-modal"
    :visible="reloginRequired"
    @update:visible="handleVisibilityChange"
    header="Sign In to Your Account"
    role="alertdialog"
    aria-describedby="login-message"
    position="top"
  >
    <template #bodyContent>
      <GbInlineMessage gbTheme="primary" class="mb-4" :inline="false" id="login-message">
        For your data protection, please enter your password to access this feature.
      </GbInlineMessage>
      <form class="login-form" @submit.prevent="attemptLogin">
        <input type="hidden" name="authenticity_token" :value="gblcs" />
        <label for="current-password" class="mb-2 mt-5">Password</label>
        <InputText
          id="current-password"
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          autocomplete="current-password"
          class="mb-2 mt-2 w-full"
        />

        <div class="form-group mb-2">
          <label for="passwordCheckbox">
            <Checkbox
              v-model="showPassword"
              :binary="true"
              inputId="passwordCheckbox"
              gbTheme="green"
              size="small"
            />
            Show Password
          </label>
        </div>

        <div class="flex items-center justify-between">
          <GbButton
            type="submit"
            label="Sign In"
            gbTheme="greenPrimary"
            size="large"
            class="my-2 h-[3rem] w-[12rem]"
            :disabled="!password || loginRequestSent"
          />

          <a :href="forgotPasswordUrl" class="text-link" target="_blank"> Forgot your password? </a>
        </div>

        <GbInlineMessage v-if="loginError" :duration="4000" gbTheme="red" class="my-4">
          {{ loginError }}
        </GbInlineMessage>
      </form>

      <div v-if="hasSSOProviders || hasGoogleOauth">
        <p class="divider sso-divider"><span>or</span></p>
        <div class="sso-buttons flex flex-col items-center">
          <template v-for="provider in ssoProviders" :key="provider.id">
            <form method="POST" :action="provider.url">
              <input type="hidden" name="authenticity_token" :value="gblcs" />
              <GbButton type="submit" gbTheme="grayOutlineSecondary" size="large" class="mb-4">
                Sign in through {{ provider.name }}
              </GbButton>
            </form>
          </template>

          <GoogleSignInButton v-if="hasGoogleOauth" />
        </div>
      </div>
    </template>
  </GbDialog>
</template>

<style scoped>
.login-form {
  font-size: 18px;
}
.divider {
  border-bottom: 1px dotted #ccc;
  margin-bottom: 30px;
  margin-left: 30px;
  margin-right: 30px;
  height: 20px;

  span {
    position: relative;
    text-align: center;
    height: 40px;
    width: 40px;
    display: block;
    margin: 0 auto;
    top: 5px;
    background: white;
    font-size: 18px;
    font-style: italic;
    color: #6e6e6e;
  }
}

label {
  font-weight: normal;
}

.sso-divider span {
  background-color: rgb(250, 250, 250);
}

.sso-buttons button {
  background-color: white;
  transition:
    background-color 0.218s,
    border-color 0.218s,
    box-shadow 0.218s;
}
.sso-buttons button:hover {
  background-color: #f0f0f0;
  box-shadow:
    0 1px 2px 0 rgba(60, 64, 67, 0.3),
    0 1px 3px 1px rgba(60, 64, 67, 0.15);
}
</style>
